import { type JSX, useMemo } from 'react'

import { Button } from '~/components/component-library/Button'
import {
  messageActionVariants,
  messageVariants,
} from '~/components/component-library/Message/styles'
import {
  type MessageAction,
  type MessageProps,
} from '~/components/component-library/Message/types'
import { CheckCircle } from '~/icons/CheckCircle'
import { Info } from '~/icons/Info'
import { OctagonAlert } from '~/icons/OctagonAlert'
import { TriangleAlert } from '~/icons/TriangleAlert'
import { cn } from '~/utils/tailwind-utils'

export {
  type MessageAction,
  type MessageProps,
} from '~/components/component-library/Message/types'

const ActionButton = ({
  action: { label, variant, ...props },
  messageVariant,
}: {
  action: MessageAction
  messageVariant: MessageProps['variant']
}) =>
  variant === 'link' ? (
    <Button
      {...props}
      type="button"
      variant="secondaryAction"
      // Take the message's color so a link reads as part of its severity, and
      // center it (Button is self-start) next to a taller button.
      className="text-inherit self-center"
    >
      {label}
    </Button>
  ) : (
    <Button
      {...props}
      type="button"
      variant="secondary"
      size="small"
      className={cn(
        'self-center',
        messageActionVariants({ variant: messageVariant }),
      )}
    >
      {label}
    </Button>
  )

export const Message = ({
  children,
  variant = 'neutral',
  title,
  meta,
  action,
}: MessageProps): JSX.Element => {
  const icon = useMemo(() => {
    if (variant === 'info') return <Info />
    if (variant === 'warning') return <TriangleAlert />
    if (variant === 'success') return <CheckCircle />
    if (variant === 'danger') return <OctagonAlert />
    return null
  }, [variant])

  // A title with body text runs taller than one line. Without both, the
  // message reads as one row, so the icon, meta, and action center against
  // the text; otherwise the icon stays on the first line.
  const hasTitleAndBody = !!title && !!children
  const isSingleRow = !hasTitleAndBody && (!!meta || !!action)
  // A titled message with body text and an action is too wide for a phone as
  // one row: the text wraps into a narrow column beside the button. Stack the
  // action under the text there and go back to one row from the sm breakpoint.
  const stacksActionOnMobile = hasTitleAndBody && !!action
  const actions = action ? [action].flat() : []

  return (
    <div
      className={cn('flex', messageVariants({ variant }), {
        'items-center': isSingleRow,
      })}
    >
      {icon && (
        <div className="items-center space-x-2 py-0.5 [&>svg]:h-4 [&>svg]:w-4">
          {icon}
        </div>
      )}
      <div
        className={cn('flex w-full min-w-0 justify-between gap-x-3', {
          'items-center': isSingleRow,
          'items-start': !isSingleRow,
          'flex-col gap-y-2 sm:flex-row': stacksActionOnMobile,
        })}
      >
        <div className="w-full min-w-0">
          {title && <div className="text-sm font-medium">{title}</div>}
          {children}
        </div>
        {meta && <div className="whitespace-nowrap text-xs">{meta}</div>}
        {actions.length > 0 && (
          <div className="flex shrink-0 items-center gap-x-2">
            {actions.map((props) => (
              <ActionButton
                key={props.label}
                action={props}
                messageVariant={variant}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
